import type { StorageKeyValue } from './StorageKey'
import { getStorageKeyTTL, getStorageKeyRemoveOnExpire } from './StorageKey'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'

const log = createLogger('StorageManager')

interface StoredValue<T = unknown> {
  value: T
  ts: number
  ttl: number
  removeOnExpire: boolean
}

interface StorageExpiration {
  expiresAt: number | null
  isValid: boolean
}

function resolveExpiration(stored: StoredValue): StorageExpiration {
  if (!Number.isFinite(stored.ts) || !Number.isFinite(stored.ttl) || stored.ttl < 0) {
    return { expiresAt: null, isValid: false }
  }

  return {
    expiresAt: stored.ttl === 0 ? null : stored.ts + stored.ttl,
    isValid: true,
  }
}

function canOverwrite(key: StorageKeyValue): boolean {
  const stored = getObject(key)

  // If key doesn't exist, allow setting
  if (!stored) {
    log.debug('Overwrite allowed because the key does not exist', key)

    return true
  }

  // If stored value is invalid, allow overwriting
  if (!resolveExpiration(stored).isValid) {
    log.debug('Overwrite allowed because the stored value has invalid TTL metadata', key)

    return true
  }

  return isTTLExpired(stored)
}

function isTTLExpired(stored: StoredValue): boolean {
  const expiration = resolveExpiration(stored)
  if (!expiration.isValid) {
    log.debug('Stored value has invalid TTL metadata')

    return true
  }

  return expiration.expiresAt !== null && Date.now() >= expiration.expiresAt
}

function set<T>(
  key: StorageKeyValue,
  value: T,
  customTTL?: number,
  force: boolean = false,
  customRemoveOnExpire?: boolean
): boolean {
  try {
    // Check if we can overwrite (unless force is true)
    if (!force && !canOverwrite(key)) {
      log.debug('Skipped setting key because its existing value is still valid', key)

      return false
    }

    const ttl = customTTL ?? getStorageKeyTTL(key)
    const removeOnExpire = customRemoveOnExpire ?? getStorageKeyRemoveOnExpire(key)

    const stored: StoredValue<T> = {
      value,
      ts: Date.now(),
      ttl,
      removeOnExpire,
    }

    localStorage.setItem(key, JSON.stringify(stored))
    log.debug('Stored value', { key, ttl, removeOnExpire })

    return true
  } catch (err) {
    log.error(`Failed to set ${key}`, err)

    return false
  }
}

// Expired values remain readable when their key explicitly disables removal on expiry.
function get<T = unknown>(key: StorageKeyValue): T | null {
  const stored = getObject<T>(key)
  if (!stored) {
    log.debug('No stored value found', key)

    return null
  }

  log.debug('Returning stored value', key)

  return stored.value
}

function getObject<T = unknown>(key: StorageKeyValue): StoredValue<T> | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) {
      log.debug('Storage key does not exist', key)

      return null
    }

    const parsed = JSON.parse(raw) as StoredValue<T>
    if (isTTLExpired(parsed) && parsed.removeOnExpire) {
      log.debug('Removing expired stored value', key)
      remove(key)

      return null
    }

    return parsed
  } catch (err) {
    log.error(`Failed to get ${key}`, err)

    return null
  }
}

function remove(key: StorageKeyValue): void {
  try {
    localStorage.removeItem(key)
    log.debug('Removed stored value', key)
  } catch (err) {
    log.error(`Failed to remove ${key}`, err)
  }
}

function clear(): void {
  try {
    localStorage.clear()
  } catch (err) {
    log.error('Failed to clear storage', err)
  }
}

function isLockActive(key: StorageKeyValue): boolean {
  const stored = getObject(key)
  if (!stored) {
    log.debug('Storage lock is not active', key)

    return false
  }

  return true
}

export const StorageManager = () => {
  return {
    set,
    get,
    remove,
    clear,
    isLockActive,
  }
}

// Time-to-live values in milliseconds
export const StorageTTL = {
  Lock: 30000, // 30 seconds
  State: 300000, // 5 minutes
  Refresh: 600000, // 10 minutes
} as const

// Re-export StorageKey for convenience
export { StorageKey } from './StorageKey'

// Also export individual functions for direct use
export { set, get, remove, clear }
